import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Package, ChefHat, Trash2, ArrowLeftRight, ClipboardList, AlertCircle, Loader2, X, Check, BarChart3 } from "lucide-react";
import MovementForm from "./inventory/MovementForm";
import EmergencyBuyForm from "./inventory/EmergencyBuyForm";
import ItemMaster from "./inventory/ItemMaster";
import ImportCentre from "./inventory/ImportCentre";
import CountSession from "./inventory/CountSession";
import InventoryReports from "./inventory/InventoryReports";

const ACTIONS = [
  { key: "receive", label: "Receive", desc: "Stock in from vendor or opening", icon: Package, color: "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" },
  { key: "issue", label: "Issue Out", desc: "Send to kitchen / staff meal", icon: ChefHat, color: "bg-[hsl(var(--brass))] text-[hsl(var(--bg))]" },
  { key: "wastage", label: "Wastage", desc: "Spoiled, expired, damaged", icon: Trash2, color: "bg-[hsl(6_62%_45%)] text-white" },
  { key: "transfer", label: "Transfer", desc: "Move between locations", icon: ArrowLeftRight, color: "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" },
  { key: "count", label: "Count", desc: "Stock take / audit", icon: ClipboardList, color: "bg-[hsl(35_75%_38%)] text-white" },
  { key: "emergency", label: "Emergency Buy", desc: "Cash purchase, no PO", icon: AlertCircle, color: "bg-[hsl(6_62%_55%)] text-white" },
];

export default function Inventory() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [view, setView] = useState("home"); // home | items | batches | approvals | settings
  const [action, setAction] = useState(null); // receive | issue | wastage | transfer | count | emergency
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [countApprovals, setCountApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [locRes, itemRes, vendorRes, batchRes] = await Promise.all([
        api.get("/staff/inventory/v2/locations"),
        api.get("/staff/inventory/v2/items?limit=500"),
        api.get("/staff/vendors", { params: { active_only: true } }),
        api.get("/staff/inventory/v2/batches", { params: { limit: 50 } }),
      ]);
      setLocations(locRes.data || []);
      setItems(itemRes.data?.items || []);
      setVendors(vendorRes.data || []);
      setBatches(batchRes.data?.rows || []);
      if (isDirector) {
        const [appRes, countAppRes, setRes] = await Promise.all([
          api.get("/staff/inventory/v2/approvals"),
          api.get("/staff/inventory/v2/approvals/counts"),
          api.get("/staff/inventory/v2/settings"),
        ]);
        setApprovals(appRes.data || []);
        setCountApprovals(countAppRes.data || []);
        setSettings(setRes.data);
      }
    } catch (e) {
      toast.error("Could not load inventory");
    } finally {
      setLoading(false);
    }
  }, [isDirector]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async (batchId) => {
    try {
      const { data } = await api.post(`/staff/inventory/v2/batches/${batchId}/post`);
      if (data.needs_approval) {
        toast.warning(`Sent to approvals: ${data.rule_hits.map(h => h.rule).join(", ")}`);
      } else {
        toast.success(`Posted — ${data.ledger_rows.length} ledger row(s)`);
      }
      setAction(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.errors) {
        toast.error(d.errors.map(e => e.detail).join(" · "));
      } else {
        toast.error(typeof d === "string" ? d : "Post failed");
      }
    }
  };

  const handleApprove = async (batchId) => {
    try {
      await api.post(`/staff/inventory/v2/batches/${batchId}/approve`);
      toast.success("Approved and posted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };

  const handleReject = async (batchId, reason) => {
    try {
      await api.post(`/staff/inventory/v2/batches/${batchId}/reject`, { reason });
      toast.success("Rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  const handleApproveCount = async (countId) => {
    try {
      const { data } = await api.post(`/staff/inventory/v2/counts/${countId}/approve`);
      toast.success(`Approved — ${data.ledger_rows} adjustment${data.ledger_rows === 1 ? "" : "s"} posted`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };

  const handleRejectCount = async (countId, reason) => {
    try {
      await api.post(`/staff/inventory/v2/counts/${countId}/reject`, { reason });
      toast.success("Count rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  const activeLocations = locations.filter(l => l.active !== false);
  const currentAction = ACTIONS.find(a => a.key === action);

  return (
    <div data-testid="inventory-module" className="min-h-screen bg-[hsl(var(--bg))]">
      {/* Mobile-first header */}
      <div className="sticky top-0 z-20 bg-[hsl(var(--bg))] border-b border-[hsl(var(--rule))] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <div className="eyebrow text-[hsl(var(--brass-2))]">EEPL Inventory</div>
            <h1 className="text-xl font-semibold">
              {view === "home" && !action && "Stock Control"}
              {view === "items" && "Item Master"}
              {view === "batches" && "Batches"}
              {view === "approvals" && "Approvals Inbox"}
              {view === "settings" && "Settings"}
              {action && currentAction?.label}
            </h1>
          </div>
          {action && (
            <button onClick={() => setAction(null)} className="text-sm text-[hsl(var(--ink-soft))] flex items-center gap-1">
              <X size={16} /> Cancel
            </button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Navigation tabs */}
        {!action && (
          <div className="flex gap-1 overflow-x-auto pb-2 mb-4 -mx-4 px-4">
            {[
              { key: "home", label: "Home" },
              { key: "items", label: "Items" },
              { key: "import", label: "Import" },
              { key: "batches", label: "Batches" },
              { key: "reports", label: "Reports" },
              ...(isDirector ? [{ key: "approvals", label: `Approvals${(approvals.length + countApprovals.length) ? ` (${approvals.length + countApprovals.length})` : ""}` }] : []),
              ...(isDirector ? [{ key: "settings", label: "Settings" }] : []),
            ].map(t => (
              <button
                key={t.key}
                data-testid={`inv-tab-${t.key}`}
                onClick={() => setView(t.key)}
                className={`px-4 py-2 text-sm whitespace-nowrap rounded-full ${view === t.key ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Action forms */}
        {action === "receive" && (
          <MovementForm type="PURCHASE_GRN" title="Receive Stock" items={items} vendors={vendors} locations={activeLocations} onDone={load} onCancel={() => setAction(null)} />
        )}
        {action === "issue" && (
          <MovementForm type="ISSUE_KITCHEN" title="Issue to Kitchen" items={items} locations={activeLocations} onDone={load} onCancel={() => setAction(null)} />
        )}
        {action === "wastage" && (
          <MovementForm type="WASTAGE" title="Record Wastage" items={items} locations={activeLocations} onDone={load} onCancel={() => setAction(null)} />
        )}
        {action === "transfer" && (
          <MovementForm type="TRANSFER_OUT" title="Transfer Stock" items={items} locations={activeLocations} onDone={load} onCancel={() => setAction(null)} />
        )}
        {action === "count" && (
          <CountSession locations={activeLocations} isDirector={isDirector} onDone={() => { setAction(null); load(); }} onCancel={() => setAction(null)} />
        )}
        {action === "emergency" && (
          <EmergencyBuyForm items={items} vendors={vendors} locations={activeLocations} settings={settings} onDone={load} onCancel={() => setAction(null)} />
        )}

        {/* Home view — six big action buttons */}
        {!action && view === "home" && (
          <div className="grid grid-cols-2 gap-3">
            {ACTIONS.map(a => {
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  data-testid={`inv-action-${a.key}`}
                  onClick={() => setAction(a.key)}
                  className={`${a.color} p-4 rounded-lg text-left flex flex-col gap-2 min-h-[100px] shadow-sm active:scale-[0.98] transition-transform`}
                >
                  <Icon size={24} />
                  <div>
                    <div className="font-semibold">{a.label}</div>
                    <div className="text-xs opacity-80">{a.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Item Master */}
        {!action && view === "items" && (
          <ItemMaster items={items} onRefresh={load} />
        )}

        {/* Import Centre */}
        {!action && view === "import" && (
          <ImportCentre items={items} locations={activeLocations} onRefresh={load} />
        )}

        {/* Reports */}
        {!action && view === "reports" && (
          <InventoryReports locations={activeLocations} isDirector={isDirector} />
        )}

        {/* Batches list */}
        {!action && view === "batches" && (
          <div className="space-y-3">
            {batches.length === 0 && <div className="p-6 text-center text-[hsl(var(--ink-soft))]">No batches yet.</div>}
            {batches.map(b => (
              <div key={b.id} data-testid={`batch-${b.id}`} className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{b.batch_no}</div>
                    <div className="text-sm font-semibold">{b.movement_type.replace(/_/g, " ")} · {b.location}{b.to_location ? ` → ${b.to_location}` : ""}</div>
                    <div className="text-xs text-[hsl(var(--ink-soft))]">{b.date} · {b.lines?.length || 0} lines · by {b.created_by}</div>
                    {b.po_number && (
                      <div className="mt-1 text-xs text-[hsl(var(--forest))]" data-testid={`batch-po-${b.id}`}>
                        Linked to PO {b.po_number}
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${b.status === "posted" ? "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]" : b.status === "pending_approval" ? "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]" : b.status === "rejected" ? "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]" : "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]"}`}>
                    {b.status.replace(/_/g, " ")}
                  </span>
                </div>
                {b.status === "draft" && (
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => handlePost(b.id)} className="text-xs px-3 py-1.5 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                      <Check size={12} className="inline mr-1" /> Post
                    </button>
                  </div>
                )}
                {b.status === "pending_approval" && b.rule_hits?.length > 0 && (
                  <div className="mt-2 text-xs text-[hsl(35_75%_38%)]">
                    {b.rule_hits.map((h, i) => <div key={i}>• {h.detail}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Approvals inbox (Director only) */}
        {!action && view === "approvals" && isDirector && (
          <div className="space-y-3">
            {approvals.length === 0 && countApprovals.length === 0 && <div className="p-6 text-center text-[hsl(var(--ink-soft))]">No pending approvals.</div>}
            {approvals.map(b => (
              <ApprovalCard key={b.id} batch={b} onApprove={() => handleApprove(b.id)} onReject={(reason) => handleReject(b.id, reason)} />
            ))}
            {countApprovals.map(c => (
              <CountApprovalCard key={c.id} count={c} onApprove={() => handleApproveCount(c.id)} onReject={(reason) => handleRejectCount(c.id, reason)} />
            ))}
          </div>
        )}

        {/* Settings (Director only) */}
        {!action && view === "settings" && isDirector && settings && (
          <InventorySettings settings={settings} onSaved={load} />
        )}
      </div>
    </div>
  );
}

function ApprovalCard({ batch, onApprove, onReject }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try { await onReject(reason); } finally { setBusy(false); setRejecting(false); }
  };

  return (
    <div data-testid={`approval-${batch.id}`} className="p-4 border border-[hsl(35_75%_38%)] bg-[hsl(38_90%_96%)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{batch.batch_no}</div>
          <div className="font-semibold">{batch.movement_type.replace(/_/g, " ")} · {batch.location}</div>
          <div className="text-xs text-[hsl(var(--ink-soft))]">{batch.date} · by {batch.submitted_by || batch.created_by}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">₹{batch.lines?.reduce((s, l) => s + (l.qty_base * (l.unit_cost || 0)), 0).toLocaleString("en-IN")}</div>
        </div>
      </div>
      <div className="mt-2 text-xs text-[hsl(35_75%_38%)] bg-[hsl(38_90%_92%)] p-2 rounded">
        {(batch.rule_hits || []).map((h, i) => <div key={i}>• {h.detail}</div>)}
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={onApprove} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
          <Check size={12} className="inline mr-1" /> Approve & Post
        </button>
        <button onClick={() => setRejecting(true)} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
          Reject
        </button>
      </div>
      {rejecting && (
        <div className="mt-3 p-3 border border-[hsl(6_62%_45%)] bg-[hsl(var(--bg))]">
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for rejection..." className="w-full p-2 text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]" rows={2} />
          <div className="mt-2 flex gap-2">
            <button onClick={submitReject} disabled={busy || !reason.trim()} className="text-xs px-3 py-1.5 rounded bg-[hsl(6_62%_45%)] text-white">Confirm Reject</button>
            <button onClick={() => setRejecting(false)} className="text-xs px-3 py-1.5 text-[hsl(var(--ink-soft))]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CountApprovalCard({ count, onApprove, onReject }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try { await onReject(reason); } finally { setBusy(false); setRejecting(false); }
  };

  const overCapLines = (count.lines || []).filter(l =>
    l.variance_value !== null && Math.abs(l.variance_value) > (count.adjustment_cap || 0)
  );

  return (
    <div data-testid={`count-approval-${count.id}`} className="p-4 border border-[hsl(35_75%_38%)] bg-[hsl(38_90%_96%)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{count.count_no}</div>
          <div className="font-semibold">
            Stock Count · {count.location}{count.category ? ` · ${count.category}` : ""}
            {count.blind && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] rounded">Blind</span>}
          </div>
          <div className="text-xs text-[hsl(var(--ink-soft))]">{count.date} · by {count.submitted_by || count.created_by}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">₹{(count.total_variance_value_abs || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(35_75%_38%)]">{count.over_limit_lines || 0} over cap</div>
        </div>
      </div>
      <button onClick={() => setExpanded(x => !x)} className="mt-2 text-xs text-[hsl(var(--forest))] underline">
        {expanded ? "Hide" : "Show"} {overCapLines.length} over-cap line{overCapLines.length === 1 ? "" : "s"}
      </button>
      {expanded && (
        <div className="mt-2 border border-[hsl(35_75%_38%)] bg-[hsl(var(--bg))] divide-y divide-[hsl(var(--rule))] max-h-48 overflow-y-auto">
          {overCapLines.map(l => (
            <div key={l.sku} className="p-2 text-xs">
              <div className="font-semibold">{l.item_name} ({l.sku})</div>
              <div className="text-[hsl(var(--ink-soft))]">
                system {l.system_qty} → counted {l.counted_qty} · Δ {l.variance > 0 ? "+" : ""}{l.variance} → ₹{l.variance_value?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button data-testid={`count-approve-${count.id}`} onClick={onApprove} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
          <Check size={12} className="inline mr-1" /> Approve & Post
        </button>
        <button data-testid={`count-reject-${count.id}`} onClick={() => setRejecting(true)} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
          Reject
        </button>
      </div>
      {rejecting && (
        <div className="mt-3 p-3 border border-[hsl(6_62%_45%)] bg-[hsl(var(--bg))]">
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for rejection..." className="w-full p-2 text-sm border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]" rows={2} />
          <div className="mt-2 flex gap-2">
            <button onClick={submitReject} disabled={busy || !reason.trim()} className="text-xs px-3 py-1.5 rounded bg-[hsl(6_62%_45%)] text-white">Confirm Reject</button>
            <button onClick={() => setRejecting(false)} className="text-xs px-3 py-1.5 text-[hsl(var(--ink-soft))]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InventorySettings({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/staff/inventory/v2/settings", form);
      toast.success("Settings saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const fields = [
    { key: "emergency_cap_per_bill", label: "Emergency cap per bill (₹)" },
    { key: "emergency_cap_per_outlet_per_day", label: "Emergency cap per outlet/day (₹)" },
    { key: "wastage_approval_above", label: "Wastage approval above (₹)" },
    { key: "adjustment_approval_above", label: "Adjustment approval above (₹)" },
    { key: "backdate_limit_days", label: "Backdate limit (days)" },
  ];

  return (
    <div className="max-w-md space-y-4">
      {fields.map(f => (
        <label key={f.key} className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          {f.label}
          <input
            type="number"
            data-testid={`inv-setting-${f.key}`}
            value={form[f.key] ?? ""}
            onChange={e => setForm({ ...form, [f.key]: Number(e.target.value) })}
            className="tbb-inp mt-1 w-full"
          />
        </label>
      ))}
      <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
        Period lock date (YYYY-MM-DD, empty = open)
        <input
          type="date"
          data-testid="inv-setting-period-lock"
          value={form.period_lock_date || ""}
          onChange={e => setForm({ ...form, period_lock_date: e.target.value || null })}
          className="tbb-inp mt-1 w-full"
        />
      </label>
      <button data-testid="inv-settings-save" onClick={save} disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">
        {saving ? "Saving..." : "Save settings"}
      </button>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
